import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { institutionApi } from "../../../modules/institution/api";
import {
  CreateUpdateInstitution,
  InstitutionFilterExportRequest,
  InstitutionFilterPageRequest,
  InstitutionFilterPageResult,
  UpdateInstitutionSettings
} from "../../../modules/institution/types";
import type { UUID } from "../../../typings/global";
import { EntityObject } from "../../types";
import messageUtils from "../../utils/messageUtils";
import { openBlobFile } from "../../utils/utils";

const FILTER_INSTITUTIONS_KEY = "filterInstitutions";

export const INSTITUTION_QUERY_KEYS = {
  filterInstitutions: (filter?: InstitutionFilterPageRequest) =>
    filter ? [FILTER_INSTITUTIONS_KEY, filter] : [FILTER_INSTITUTIONS_KEY],
  downloadInstitutionsExport: () => ["downloadInstitutionsExport"],
  getInstitution: (id: UUID) => ["getInstitution", id],
  createInstitution: () => ["createInstitution"],
  updateInstitution: () => ["updateInstitution"],
  updateInstitutionSettings: () => ["updateInstitutionSettings"],
  deleteInstitution: () => ["deleteInstitution"]
};

export const useFilterInstitutions = (filter: InstitutionFilterPageRequest) => {
  return useQuery({
    queryKey: INSTITUTION_QUERY_KEYS.filterInstitutions(filter),
    queryFn: async () => {
      const { data } = await institutionApi.filterInstitutions(filter);

      return data;
    }
  });
};

export const useDownloadInstitutionsExport = () => {
  return useMutation({
    mutationKey: INSTITUTION_QUERY_KEYS.downloadInstitutionsExport(),
    mutationFn: async (payload: InstitutionFilterExportRequest) => {
      const response = await institutionApi.downloadInstitutionsExport(payload);
      openBlobFile(response, true);
    }
  });
};

export const useGetInstitution = (id: UUID) => {
  return useQuery({
    queryKey: INSTITUTION_QUERY_KEYS.getInstitution(id),
    queryFn: async () => {
      const { data } = await institutionApi.getInstitution({ id });

      return data;
    }
  });
};

export const useCreateInstitution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: INSTITUTION_QUERY_KEYS.createInstitution(),
    mutationFn: async (payload: CreateUpdateInstitution) => {
      const { data } = await institutionApi.createInstitution(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INSTITUTION_QUERY_KEYS.filterInstitutions() });
      messageUtils.itemCreatedNotification();
    }
  });
};

export const useUpdateInstitution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: INSTITUTION_QUERY_KEYS.updateInstitution(),
    mutationFn: async (payload: EntityObject<CreateUpdateInstitution>) => {
      const { data } = await institutionApi.updateInstitution(payload);

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INSTITUTION_QUERY_KEYS.filterInstitutions() });
      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useUpdateInstitutionSettings = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: INSTITUTION_QUERY_KEYS.updateInstitutionSettings(),
    mutationFn: async (payload: EntityObject<UpdateInstitutionSettings>) => {
      const { data } = await institutionApi.updateInstitutionSettings(payload);

      return data;
    },
    onSuccess: (data, mutatedPayload) => {
      queryClient.setQueriesData(
        { predicate: ({ queryKey }) => queryKey?.[0] === FILTER_INSTITUTIONS_KEY },
        (oldData: InstitutionFilterPageResult) => ({
          ...oldData,
          pageData: oldData.pageData.map(item => {
            if (item.id === mutatedPayload.id) {
              return {
                ...item,
                settings: data
              };
            }

            return item;
          })
        })
      );

      messageUtils.itemUpdatedNotification();
    }
  });
};

export const useDeleteInstitution = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: INSTITUTION_QUERY_KEYS.deleteInstitution(),
    mutationFn: (id: UUID) => {
      return institutionApi.deleteInstitution({ id });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: INSTITUTION_QUERY_KEYS.filterInstitutions() });
      messageUtils.itemDeletedNotification();
    }
  });
};
